import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { cancelSchedule, createSchedule, listEmployees, listSchedules, listShifts, reassignSchedule } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Employee, Schedule, Shift } from '../../types/domain';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtTime(t: string): string {
  return t.slice(0, 5);
}

function fmtWorkDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('id-ID', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Schedule / Kalender Kerja workspace (FASE 9 closure correction, migration
 * 0042). Per the authoritative V5 spec, schedule authoring IS part of FASE
 * 9 — this is the missing enterprise Schedule workspace: date-oriented
 * view, employee/shift filters, create/reassign/cancel through checked RPC
 * paths only (fn_create_schedule_checked / fn_reassign_schedule_checked /
 * fn_cancel_schedule_checked). No raw table mutation from the browser.
 *
 * Branch-scoped: re-fetches on Cabang Aktif change (proves the branch
 * selector re-fetches schedule data correctly, not just a stale client
 * filter).
 */
export default function SchedulePage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<Schedule | null>(null);

  const [dateFrom, setDateFrom] = useState(() => isoDate(new Date()));
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 13);
    return isoDate(d);
  });

  const canCreate = hasPermission('schedule', 'create');
  const canUpdate = hasPermission('schedule', 'update');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [scheduleRows, empRows, shiftRows] = await Promise.all([
        listSchedules(activeBranchId ?? undefined, dateFrom, dateTo),
        listEmployees(activeBranchId ?? undefined),
        listShifts(activeBranchId ?? undefined),
      ]);
      setSchedules(scheduleRows);
      setEmployees(empRows);
      setShifts(shiftRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Re-fetches whenever the active branch OR the visible date window
  // changes — this workspace never silently reuses stale cross-branch data.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, dateFrom, dateTo]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? id;
  const shiftLabel = (id: string) => {
    const s = shifts.find((sh) => sh.shift_id === id);
    if (!s) return id;
    return `${s.name} (${fmtTime(s.start_time)}–${fmtTime(s.end_time)}${s.crosses_midnight ? ', lintas hari' : ''})`;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schedules.filter((s) => {
      if (q && !employeeName(s.employee_id).toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, search, statusFilter, employees]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);

  const plannedCount = schedules.filter((s) => s.status === 'PLANNED').length;
  const cancelledCount = schedules.filter((s) => s.status === 'CANCELLED').length;

  return (
    <div>
      <PageHeader title="Jadwal Kerja (Schedule)" subtitle="Penjadwalan karyawan per shift — dasar kehadiran & keterlambatan. Migrasi 0042." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Jadwal baru'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewScheduleForm
          employees={employees}
          shifts={shifts}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Jadwal', value: schedules.length },
              { label: 'Terjadwal', value: plannedCount, tone: 'success' as const },
              { label: 'Dibatalkan', value: cancelledCount, tone: 'neutral' as const },
            ]}
          />
          <div className="alex-card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ width: 160 }}>
              <FieldLabel label="Dari tanggal">
                <TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </FieldLabel>
            </div>
            <div style={{ width: 160 }}>
              <FieldLabel label="Sampai tanggal">
                <TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </FieldLabel>
            </div>
          </div>
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama karyawan..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="PLANNED">Terjadwal</option>
                <option value="CANCELLED">Dibatalkan</option>
              </Select>
            }
          />
          {schedules.length === 0 && <EmptyState text="Belum ada jadwal pada rentang tanggal ini." />}
          {schedules.length > 0 && total === 0 && <EmptyState text="Tidak ada jadwal yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tanggal</th>
                  <th style={thStyle}>Karyawan</th>
                  <th style={thStyle}>Shift</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => (
                  <ClickableRow key={s.schedule_id} onClick={() => setSelected(s)}>
                    <td style={tdStyle}>{fmtWorkDate(s.work_date)}</td>
                    <td style={tdStyle}>{employeeName(s.employee_id)}</td>
                    <td style={tdStyle}>{shiftLabel(s.shift_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={s.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <ScheduleDetailDrawer
        schedule={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        employees={employees}
        employeeName={employeeName}
        shiftLabel={shiftLabel}
        canUpdate={canUpdate}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewScheduleForm({ employees, shifts, onCreated }: { employees: Employee[]; shifts: Shift[]; onCreated: () => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [workDate, setWorkDate] = useState(() => isoDate(new Date()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeShifts = shifts.filter((s) => s.status === 'ACTIVE');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createSchedule(employeeId, shiftId, workDate);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Karyawan">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Shift">
          <Select value={shiftId} onChange={(e) => setShiftId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {activeShifts.map((s) => (
              <option key={s.shift_id} value={s.shift_id}>
                {s.name} ({fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                {s.crosses_midnight ? ', lintas hari' : ''})
              </option>
            ))}
          </Select>
        </FieldLabel>
        {activeShifts.length === 0 && (
          <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>Belum ada shift aktif — buat shift di Shift Master dahulu.</div>
        )}
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Tanggal Kerja">
          <TextInput type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function ScheduleDetailDrawer({
  schedule,
  open,
  onClose,
  employees,
  employeeName,
  shiftLabel,
  canUpdate,
  onChanged,
}: {
  schedule: Schedule | null;
  open: boolean;
  onClose: () => void;
  employees: Employee[];
  employeeName: (id: string) => string;
  shiftLabel: (id: string) => string;
  canUpdate: boolean;
  onChanged: () => void;
}) {
  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNewEmployeeId('');
    setError(null);
  }, [schedule?.schedule_id]);

  if (!schedule) return null;

  const isPlanned = schedule.status === 'PLANNED';

  const handleReassign = async () => {
    if (!newEmployeeId) {
      setError('Pilih karyawan pengganti terlebih dahulu.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await reassignSchedule(schedule.schedule_id, newEmployeeId);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await cancelSchedule(schedule.schedule_id);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Detail Jadwal">
      <DrawerField label="Tanggal Kerja" value={schedule.work_date} />
      <DrawerField label="Karyawan" value={employeeName(schedule.employee_id)} />
      <DrawerField label="Shift" value={shiftLabel(schedule.shift_id)} />
      <DrawerField label="Status" value={<StatusBadge status={schedule.status} />} />

      {canUpdate && isPlanned && (
        <>
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
            <FieldLabel label="Reassign ke karyawan lain">
              <Select value={newEmployeeId} onChange={(e) => setNewEmployeeId(e.target.value)}>
                <option value="">Pilih...</option>
                {employees
                  .filter((emp) => emp.employee_id !== schedule.employee_id)
                  .map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_no} — {emp.legal_name}
                    </option>
                  ))}
              </Select>
            </FieldLabel>
            <Button variant="secondary" onClick={() => void handleReassign()} loading={submitting} style={{ marginTop: 8 }}>
              Reassign
            </Button>
          </div>
          <div style={{ marginTop: 12 }}>
            <Button variant="ghost" onClick={() => void handleCancel()} loading={submitting}>
              Batalkan Jadwal
            </Button>
          </div>
        </>
      )}
      {!isPlanned && (
        <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 16 }}>
          Jadwal ini sudah {schedule.status === 'CANCELLED' ? 'dibatalkan' : 'tidak aktif'} — tidak bisa direassign atau dibatalkan lagi.
        </p>
      )}
      {error && <ErrorNote text={error} />}
    </Drawer>
  );
}
